import React, { useEffect, useState } from 'react';
import { supabase } from '../App';
import ServicesGraphic from '../assets/images/solutions.jpg';

const fallbackServices = [
  { 
    title: "Software Development", 
    desc: "Custom software solutions tailored to your unique business needs, including web, mobile, and enterprise applications.",
    services_bg_image: "/images/placeholder1.png"
  },
  { 
    title: "Network Solutions", 
    desc: "Design, implementation, and management of secure and efficient network infrastructures.",
    services_bg_image: "/images/placeholder2.png"
  },
  { 
    title: "IT Support & Maintenance", 
    desc: "Reliable technical support and proactive maintenance to ensure your systems run smoothly.",
    services_bg_image: "/images/placeholder3.png"
  },
  { 
    title: "Cloud Services", 
    desc: "Migration, management, and optimization of cloud infrastructure for scalable performance.",
    services_bg_image: "/images/placeholder4.png"
  },
  { 
    title: "Cybersecurity", 
    desc: "Protecting your digital assets with comprehensive security strategies and advanced threat detection.",
    services_bg_image: "/images/placeholder5.png"
  },
  { 
    title: "Custom Solutions", 
    desc: "Don't see what you need? We specialize in tailoring bespoke IT solutions to meet your exact requirements.",
    services_bg_image: "/images/placeholder6.png"
  }
];

function Services() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove('opacity-0', 'translate-y-12');
            entry.target.classList.add('opacity-100', 'translate-y-0');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    const hiddenElements = document.querySelectorAll('.animate-on-scroll');
    hiddenElements.forEach((el) => observer.observe(el));

    return () => {
      hiddenElements.forEach((el) => observer.unobserve(el));
    };
  }, [services]); 

  useEffect(() => {
    async function fetchServices() {
      try {
        setLoading(true);
        const { data } = await supabase.from('services').select('*').order('id', { ascending: true });
        
        if (data && data.length > 0) {
          setServices(data);
        } else {
          setServices(fallbackServices);
        }
      } catch (e) {
        setServices(fallbackServices);
      } finally {
        setLoading(false);
      }
    }
    if (supabase) fetchServices();
  }, []);

  const iconMap = {
    "Software Development": <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"></path></svg>,
    "Network Solutions": <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"></path></svg>,
    "IT Support & Maintenance": <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>,
    "Cloud Services": <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z"></path></svg>,
    "Cybersecurity": <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path></svg>,
    "Custom Solutions": <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"></path></svg>
  };

  return (
    <div className="min-h-screen pt-24 pb-20 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="relative overflow-hidden rounded-2xl bg-slate-900 border border-slate-800 p-10 md:p-16 mb-12 shadow-lg animate-on-scroll opacity-0 translate-y-12 transition-all duration-[1000ms] ease-out">
          <div className="absolute inset-0 z-0">
            <img 
              src={ServicesGraphic} 
              className="w-full h-full object-cover opacity-40 mix-blend-screen" 
              alt="Network Architecture Background" 
            />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-900/90 to-transparent"></div>
          </div>
          <div className="relative z-10 md:w-2/3">
            <p className="text-blue-500 text-xs uppercase tracking-[0.3em] font-bold mb-3">What We Deliver</p>
            <h1 className="text-4xl md:text-5xl font-black text-white tracking-tighter mb-4">Solutions Designed For You</h1>
            <p className="text-slate-300 text-lg font-medium leading-relaxed max-w-2xl mt-4">
              Comprehensive, scalable, and secure technology services engineered for the demands of the modern enterprise.
            </p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {loading ? (
            [1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-slate-900/50 backdrop-blur-md animate-pulse border border-slate-800 rounded-2xl h-64"></div>
            ))
          ) : (
            services.map((service, i) => (
              <div 
                key={i} 
                style={{ transitionDelay: `${i * 150}ms` }}
                className="relative overflow-hidden animate-on-scroll opacity-0 translate-y-12 bg-slate-900/60 backdrop-blur-md border border-slate-800 rounded-2xl hover:bg-slate-800/80 hover:border-blue-500/50 hover:-translate-y-2 hover:shadow-[0_10px_30px_rgba(37,99,235,0.15)] transition-all duration-500 group cursor-default flex flex-col"
              >
                <div className="absolute inset-0 z-0">
                  {service.services_bg_image && (
                    <img 
                      src={service.services_bg_image} 
                      className="w-full h-full object-cover mix-blend-screen transition-all duration-500 opacity-20 group-hover:opacity-40 blur-sm group-hover:blur-none" 
                      alt="" 
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-br from-slate-950/70 via-slate-900/50 to-transparent"></div>
                </div>
                <div className="relative z-10 p-8 h-full flex flex-col">
                  <div className="mb-6 inline-flex p-3 bg-blue-900/30 rounded-xl text-blue-500 group-hover:bg-blue-600 group-hover:text-white transition-all duration-300 self-start">
                    {iconMap[service.title] || <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path></svg>}
                  </div>
                  <h4 className="text-xl font-bold text-white mb-3 group-hover:text-blue-400 transition-colors duration-300">
                    {service.title}
                  </h4>
                  <p className="text-slate-400 text-sm leading-relaxed group-hover:text-slate-300 transition-colors duration-300 flex-grow">
                    {service.desc || service.description}
                  </p>
                  <div className="mt-8 h-[2px] w-8 bg-slate-700 group-hover:bg-blue-500 group-hover:w-16 transition-all duration-500"></div>
                </div>
              </div>
            ))
          )}
        </div>

      </div>
    </div>
  );
}

export default Services;